import React, { useState, useEffect } from "react";
import QrReader from "react-qr-scanner";
import axios from "axios";
import { toast } from "react-hot-toast";
import { ScanLine, X, Camera, Loader2 } from "lucide-react";

const EventQrScanner = ({ afterScanMethod }) => {
  const [open, setOpen] = useState(false);
  const [reopenAfterScan, setReopenAfterScan] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem("token");

  const handleScan = (data) => {
    if (data) {
      if (data?.text?.includes("===")) {
        setIsLoading(true);
        const [alumniId, eventId] = data.text.split("===");

        axios
          .post(
            `${process.env.REACT_APP_BASE_URL}/api/events/verifyRegistration`,
            { alumniId, eventId },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
          .then((res) => {
            if (res?.data?.success) {
              toast.custom(
                (t) => (
                  <div className="bg-white rounded-lg shadow-lg p-4 max-w-md w-full mx-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Scan Successful</h3>
                      <button onClick={() => toast.dismiss(t.id)}>
                        <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                      </button>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <img
                          src={res?.data?.alumni?.photo || '/api/placeholder/64/64'}
                          alt="Profile"
                          className="w-16 h-16 rounded-full object-cover border-2 border-blue-500"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {res?.data?.alumni?.firstName} {res?.data?.alumni?.lastName}
                        </p>
                        <p className="text-sm text-gray-600">{res?.data?.alumni?.email}</p>
                        <div className="mt-2 p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-700">{res?.data?.message}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
                { duration: 3000, position: "bottom-center" }
              );
              afterScanMethod();
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.error || "Scanning failed");
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        toast.error("Invalid QR code");
      }

      setReopenAfterScan(true);
      setOpen(false);
    }
  };

  const handleError = (err) => {
    toast.error("Camera error: " + err.message);
  };

  useEffect(() => {
    if (!open && reopenAfterScan) {
      const timer = setTimeout(() => {
        setOpen(true);
        setReopenAfterScan(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [open, reopenAfterScan]);

  const toggleCamera = () => {
    setFacingMode(prev => prev === "environment" ? "user" : "environment");
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <ScanLine className="w-5 h-5 mr-2 text-gray-500" />
        Scan QR
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Scan Attendance QR Code</h2>
              <button
                onClick={() => {
                  setReopenAfterScan(false);
                  setOpen(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="relative">
              <div className="aspect-video rounded-lg overflow-hidden border-2 border-gray-200">
                {isLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                ) : (
                  <QrReader
                    delay={300}
                    onError={handleError}
                    onScan={handleScan}
                    facingMode={facingMode}
                    className="w-full h-full"
                    style={{ width: '100%' }}
                  />
                )}
              </div>

              <div className="absolute bottom-4 right-4 flex space-x-2">
                <button
                  onClick={toggleCamera}
                  className="p-3 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
                >
                  <Camera className="w-6 h-6 text-gray-700" />
                </button>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-500 text-center">
                Position the QR code within the camera frame to scan
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventQrScanner;